import { isMessage } from '@bufbuild/protobuf'
import { TimestampSchema } from '@bufbuild/protobuf/wkt'
import type {
  Column,
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
} from 'ag-grid-community'
import _ from 'lodash'
import { useCallback } from 'react'

import { AlertCountStat } from '@/gen/inventory/v1/alert_count_stat_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb.ts'

import { riskLevelLabel } from '@/const/label.ts'

import { useReportErrorsCallback } from '@/lib/connect/error-reporting'
import { formatDate, formatTimestamp } from '@/lib/date/date'

import { TABLE_LABELS } from '@/components/lemmala-design-system/table/labels'
import type {
  CsvExportColumnContext,
  CsvExportManager,
} from '@/components/lemmala-design-system/table/types'
import { escapeCsvFormula } from '@/components/lemmala-design-system/table/utils/csv-export'

/**
 * Configuration for CSV export functionality.
 */
export interface CsvExportConfig {
  /**
   * Function to generate filename for the CSV export.
   * If not provided, defaults to a timestamped filename.
   */
  getFileName?: () => string

  /**
   * Custom header processing function.
   * If not provided, uses column headerName or colId as fallback.
   */
  processHeaderCallback?: (params: ProcessHeaderForExportParams) => string

  /**
   * Additional export options to merge with defaults.
   */
  exportOptions?: Record<string, unknown>

  /**
   * Custom export function that completely overrides the default CSV export behavior.
   * Use this when you need complete control over the export process (e.g., async data fetching).
   * When provided, all other config options are ignored.
   */
  customExportFunction?: () => void | Promise<void>

  /**
   * Loading state for custom export function.
   * Used to show loading indicator on the export button.
   */
  isExporting?: boolean

  /**
   * Prefix formula-looking cells (= + - @) so spreadsheets show them as text. For tables of vendor-typed text.
   */
  escapeFormulas?: boolean
}

/**
 * Generic CSV export hook for AG Grid tables.
 *
 * This hook provides the same CSV export functionality as the original useCsvExport
 * but with generic typing and configurable filename generation.
 *
 * Features:
 * - Exports only visible and displayed (filtered) rows
 * - Processes headers to be human-readable
 * - Configurable filename generation
 * - Proper error handling with console warnings/errors
 * - Loading state tracking
 *
 * @example
 * ```tsx
 * const csvExport = useAgCsvExport(gridApi, {
 *   getFileName: () => `company-inventory-${Date.now()}.csv`,
 *   processHeaderCallback: (params) => params.column.getColDef().headerName || 'Unknown'
 * })
 *
 * // Export data
 * csvExport.exportToCsv()
 * ```
 */
export const useAgCsvExport = <TRowData = unknown>(
  gridApi: GridApi<TRowData> | null,
  config?: CsvExportConfig,
): CsvExportManager => {
  const reportErrors = useReportErrorsCallback()
  const exportToCsv = useCallback(() => {
    // If a custom export function is provided, use it instead of the default behavior
    if (config?.customExportFunction) {
      config.customExportFunction()
      return
    }

    if (!gridApi) {
      reportErrors(new Error('[AgCsvExport] Grid API not available for CSV export'))
      return
    }

    try {
      // Default filename generator
      const defaultFileName = () => `lema-inventory-${formatDate(new Date(), 'monthDayYear')}.csv`

      // Default header processor with better fallbacks
      const defaultHeaderProcessor = (params: ProcessHeaderForExportParams) => {
        const colDef = params.column.getColDef()
        const colId = params.column.getColId()
        const headerName = typeof colDef.headerName === 'string' ? colDef.headerName : ''

        // If headerName is a non-empty string after trimming, use it
        if (headerName && headerName.trim().length > 0) return headerName

        // Otherwise, prefer headerTooltip if available (e.g., inherent risk column)
        const tooltip = (colDef.headerTooltip as string) || ''
        if (tooltip && tooltip.trim().length > 0) return tooltip

        // Fallback: prettify the column id (e.g., "profile.name" => "Profile Name")
        const prettified = (colId || TABLE_LABELS.unknownColumn)
          .split(/[._-]/)
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
        return prettified || TABLE_LABELS.unknownColumn
      }

      // Custom cell processor for CSV export only (keeps UI formatters/renderers untouched)
      const processCellForCsv = (params: ProcessCellForExportParams) => {
        const colId = params.column.getColId()
        const row = (params.node?.data || ({} as unknown)) as Record<string, unknown>

        // Format protobuf Timestamps as ISO dates so spreadsheets parse them as dates
        if (isMessage(params.value, TimestampSchema)) {
          return formatTimestamp(params.value, 'yyyyMMdd') || ''
        }

        // Replace UUID for scorer with empty or a more friendly placeholder
        if (colId === 'lastScorer') {
          // We don't have the user name in row data here; avoid exporting raw UUID
          return ''
        }

        // Smart evidence request: export as "N Waiting for recipient"
        if (colId === 'smartEvidence') {
          const n = Number(params.value || 0)
          return n > 0 ? `${n} Waiting for recipient` : ''
        }

        // For alertsByLevel, convert to object and stringify (e.g. {"Critical":1,"High":1,"Medium":3,"Low":0})
        if (colId === 'alertsByLevel') {
          const alertsByLevel = (row.alertsByLevel ?? []) as AlertCountStat[]
          const alertObject = alertsByLevel.reduce(
            (acc, { riskLevel, count }) => {
              acc[riskLevelLabel[riskLevel]] = count
              return acc
            },
            {
              [riskLevelLabel[RiskLevel.CRITICAL]]: 0,
              [riskLevelLabel[RiskLevel.HIGH]]: 0,
              [riskLevelLabel[RiskLevel.MEDIUM]]: 0,
              [riskLevelLabel[RiskLevel.LOW]]: 0,
            } as Record<string, number>,
          )
          return JSON.stringify(alertObject)
        }

        // For arrays (tags, sources, dataTypes) ensure CSV-friendly joins
        if (Array.isArray(params.value)) {
          return (params.value as unknown[]).join(', ')
        }

        // Default: use the already formatted value
        return params.value
      }

      // Base export options with sensible defaults
      // Filter-only columns to exclude from CSV (explicit list to avoid exporting hidden filter helpers)
      const EXCLUDED_COL_IDS = new Set([
        'lifecycleBase',
        'isSubprocessor',
        'projects',
        'questionnaireFilter',
        // Discovery filters users and devices apart; the Reach column already exports the count.
        'reachDevices',
        'reachUsers',
        '__filler__',
        '__groupHeader',
      ])

      const exportableColumns = (gridApi.getColumns() ?? []).filter(
        (col) => !EXCLUDED_COL_IDS.has(col.getColId()),
      )
      // Saved layouts move picker-excluded defs first, so export-only columns name their anchor.
      const exportAfter = (col: Column) => {
        const context: CsvExportColumnContext | undefined = col.getColDef().context
        return context?.csvExportAfter
      }
      const [followers, leaders] = _.partition(exportableColumns, exportAfter)
      const orderedColumns = leaders.flatMap((col) => [
        col,
        ...followers.filter((follower) => exportAfter(follower) === col.getColId()),
      ])
      const orphanedFollowers = followers.filter((follower) => !orderedColumns.includes(follower))

      const baseOptions = {
        // Include all columns, but respect per-column suppressCsvExport flags
        // We'll set allColumns=true and also provide columnKeys that exclude any columns
        // with suppressCsvExport=true to ensure filter-only columns are not exported.
        allColumns: true,
        columnKeys: [...orderedColumns, ...orphanedFollowers].map((col) => col.getColId()),

        // Use configured filename generator or default
        fileName: config?.getFileName?.() || defaultFileName(),

        // Process headers to be more readable
        processHeaderCallback: config?.processHeaderCallback || defaultHeaderProcessor,

        // Process cells specifically for export
        processCellCallback: config?.escapeFormulas
          ? (params: ProcessCellForExportParams) => escapeCsvFormula(processCellForCsv(params))
          : processCellForCsv,

        // Skip pinned rows if any
        skipPinnedTop: true,
        skipPinnedBottom: true,

        // Export only filtered/displayed data
        onlySelected: false,

        // We'll export hidden columns too (handled by allColumns=true above)
      }

      // Merge with any additional options from config
      const finalOptions = {
        ...baseOptions,
        ...config?.exportOptions,
      }

      gridApi.exportDataAsCsv(finalOptions)
    } catch (error) {
      reportErrors(error instanceof Error ? error : new Error('[AgCsvExport] Failed to export CSV'))
    }
  }, [gridApi, config, reportErrors])

  // Return loading state from config if using custom export function, otherwise false
  return {
    exportToCsv,
    isExporting: config?.isExporting ?? false,
  }
}
